<template>
  <div class="milestone-progress-reports">
    <header>
      <div class="milestone-progress-reports__select">
        <p>{{ $t(`chartsDashboard.select[0]`) }}</p>
        <ElSelect
          v-model="selectedListUid"
          :disabled="companyListDisabled"
          filterable
          default-first-option
          popper-class="milestone-progress-reports__select-list"
          data-testid="milestone-progress-reports-select"
        >
          <ElOption
            v-for="list in companyLists"
            :key="list.uid"
            :value="list.uid"
            :label="list.title"
          />
        </ElSelect>
      </div>
    </header>

    <!-- No Lists Present -->
    <div
      v-if="companyLists?.length === 0 && proUser"
      class="milestone-progress-reports__nolist"
    >
      <div class="milestone-progress-reports__nolist__icon">
        <img src="img/no-matches.svg" />
      </div>
      <div class="milestone-progress-reports__nolist__text">
        <span>No lists available.</span>
        <span>
          You havent created any lists yet. Start by creating a new list to keep
          track of relevant companies
        </span>
      </div>
      <PxButton
        type="green"
        class="milestone-progress-reports__nolist__cta"
        @click="newListHandler"
      >
        <PxIcon :size="18" name="plus-white" />
        NEW LIST
      </PxButton>
    </div>
    <!-- No Lists Present -->

    <!-- No Companies In The Slected List -->
    <div
      v-if="selectedList && selectedList.companies.length === 0 && proUser"
      class="milestone-progress-reports__nocompanies"
    >
      <div class="milestone-progress-reports__nocompanies__icon">
        <img src="img/no-matches.svg" />
      </div>
      <div class="milestone-progress-reports__nocompanies__text">
        <span>No companies in the list.</span>
        <span>
          Start adding companies to track your network’s milestone progress.
        </span>
      </div>
      <PxButton
        type="green"
        class="milestone-progress-reports__nocompanies__cta"
        @click="openListHandler"
      >
        OPEN LIST
      </PxButton>
    </div>
    <!-- No Companies In The Slected List -->

    <!-- Milestone Data -->
    <div
      v-if="(selectedList && selectedList.companies.length > 0) || !proUser"
      v-loading="loading"
      element-loading-background="#fafafa"
      class="mileston-progress-reports__content"
      :class="{
        'mileston-progress-reports__loading': loading,
      }"
    >
      <template v-if="!loading">
        <div class="milestone-progress-reports__wrapper">
          <div class="milestone-progress-reports__header">
            <PxNavigationTab
              :navigation-tabs="navigationTabItems"
              :selected-tab="selectedTab"
              @update:selected-tab="handleTabSelection"
            />
          </div>

          <div class="milestone-progress-reports__content">
            <div class="milestone-progress-reports__title">
              <div
                v-if="selectedTab === 'levels-date-range'"
                class="milestone-progress-reports__title-primary"
              >
                <h1>
                  {{ selectedList?.title }} company levels at start vs at end
                </h1>
                <p>
                  Level progress represents growth across the eight VIL
                  categories.
                </p>
              </div>

              <div
                v-if="selectedTab === 'levels-milestone'"
                class="milestone-progress-reports__title-primary"
              >
                <h1>
                  {{ selectedList?.title }} company levels & milestones achieved
                </h1>
                <p>
                  Level progress represents growth across the eight VIL
                  categories.
                </p>
              </div>
              <div class="milestone-progress-reports__title-secondary">
                <h1>
                  {{ assessmentData?.assessmentDates?.start }} -
                  {{ assessmentData?.assessmentDates?.end }}
                </h1>
              </div>
            </div>
            <div class="milestone-progress-reports__charts">
              <!-- CHART LEVELS OVER TIME -->
              <template
                v-if="selectedTab === MILESTONE_DETAILS_TABS.LEVELS_OVER_TIME"
              >
                <!-- @vue-ignore -->
                <!-- <PxCustomBarChart
                  :type="levelsOverTimeChart?.type"
                  :data="levelsOverTimeChart?.data"
                  :options="levelsOverTimeChart?.options"
                  :plugins="levelsOverTimeChart?.plugins"
                /> -->
              </template>
              <!-- CHART LEVELS -->
              <template v-if="selectedTab === MILESTONE_DETAILS_TABS.LEVELS">
                <!-- @vue-ignore -->
                <PxCustomBarChart
                  :type="levelsChart?.type"
                  :data="levelsChart?.data"
                  :options="levelsChart?.options"
                  :plugins="levelsChart?.plugins"
                />
              </template>

              <!-- CHART MILESTONES -->
              <template
                v-if="selectedTab === MILESTONE_DETAILS_TABS.MILESTONES"
              >
                <div class="milestone-progress-reports__chart-label">
                  <span>Levels</span>
                  <span>Milestone</span>
                </div>
                <!-- @vue-ignore -->
                <PxCustomBarChart
                  :type="milestonesChart?.type"
                  :data="milestonesChart?.data"
                  :options="milestonesChart?.options"
                  :plugins="milestonesChart?.plugins"
                />
              </template>
            </div>
            <div
              v-if="progressTableData"
              class="milestone-progress-reports__tables"
            >
              <PxTable :data="progressTableData" />
            </div>
          </div>

          <div class="milestone-progress-reports__data">
            <div class="milestone-progress-reports__data__inner">
              <div
                v-if="highlightsData && highlightsData.length"
                class="milestone-progress-reports__data__highlights"
              >
                <div
                  v-for="(data, index) in highlightsData"
                  :key="index"
                  class="milestone-progress-reports__highlights__section"
                >
                  <span
                    class="milestone-progress-reports__highlights__title"
                    v-html="`${data.icon} ${data.title}`"
                  ></span>
                  <div
                    v-for="(item, itemIndex) in data.items"
                    :key="itemIndex"
                    class="milestone-progress-reports__highlights__sub-item"
                  >
                    <span>{{ item.title }}</span>
                    <span>{{ item.subtitle }}</span>
                  </div>
                </div>
              </div>

              <div class="milestone-progress-reports__data__assesment">
                <div
                  class="milestone-progress-reports__data__assesment__header"
                >
                  Assesment Data
                </div>
                <div
                  v-if="assessmentData"
                  class="milestone-progress-reports__data__assesment__table"
                  assessmentData
                >
                  <PxTable :data="assessmentData" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <template v-if="showEmptyState">
          <RequestProcessesModal v-model:visibility="showEmptyState" />
          <div class="process-reports__overlay"></div>
        </template>
      </template>
    </div>
    <!-- Milestone Data -->
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref } from "vue";
import { watch } from "vue";
import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

import PxTable from "@/components/px-table/px-table.vue";
import PxButton from "@/components/px-button/px-button.vue";
import PxNavigationTab from "@/components/px-navigation-tab/px-navigation-tab.vue";
import PxCustomBarChart from "@/components/charts/custom-bar-chart/custom-bar-chart.vue";

import { userManager } from "@/modules/authentication/user-manager";
import { CompanyListState } from "@/modules/company-lists/services/store/company-list/company-list.modules";
import { createProgressTableData } from "@/modules/charts-dashboard/utils/milestone-reports.table";
import { createHiglightsData } from "@/modules/charts-dashboard/utils/milestone-reports.table";
import { MilestoneReportsState } from "@/modules/charts-dashboard/services/store/milestone/milestone.module";
import { createLevelsChartData } from "@/modules/charts-dashboard/utils/milestone-reports.chart";
import { createMilestonesChartData } from "@/modules/charts-dashboard/utils/milestone-reports.chart";
import { createAssessmentTableData } from "@/modules/charts-dashboard/utils/milestone-reports.table";
// import { createLevelsOverTimeChartData } from "@/modules/charts-dashboard/utils/milestone-reports.chart";

import type { IMilestoneReports } from "@/modules/charts-dashboard/services/data/milestone-reports/milestone.interface";
import type { TMilestoneDetailsTab } from "@/modules/milestone-planner/services/data/milestones/milestone.interface";

import { MILESTONE_DETAILS_TABS } from "@/modules/milestone-planner/services/data/milestones/milestone.interface";

const store = useStore();
const router = useRouter();

const milestoneAndProgressReportData = computed<IMilestoneReports>(() =>
  store.get(MilestoneReportsState.Getter.VALUE),
);

const navigationTabItems = ref([
  // {
  //   id: MILESTONE_DETAILS_TABS.LEVELS_OVER_TIME,
  //   title: "Levels over Time",
  // },
  {
    id: MILESTONE_DETAILS_TABS.LEVELS,
    title: "Levels at Start vs End",
  },
  {
    id: MILESTONE_DETAILS_TABS.MILESTONES,
    title: "Levels & Milestones Achieved",
  },
]);

// The company lists
const companyLists = computed(() => store.get(CompanyListState.Getter.VALUES));
// weather the current user has pro subscription or not
const proUser = computed(userManager.isProUser);
// The currently selected company list
const selectedList = ref();
const selectedListUid = ref();
// Disable company list selection
const companyListDisabled = ref(false);
const loading = ref(false);
const showEmptyState = ref(false);
const selectedTab = ref<TMilestoneDetailsTab>(navigationTabItems.value[0].id);
// Pre calculated data for the different charts
const levelsChart = ref<any>();
const milestonesChart = ref<any>();
// const levelsOverTimeChart = ref<any>();
// Precalculated data for the tables
const highlightsData = ref<any>();
const assessmentData = ref<any>();
const progressTableData = ref<any>();

function handleTabSelection(val: any) {
  selectedTab.value = val;
}

function newListHandler() {
  router.push({ path: "/lists", query: { form_open: "true" } });
}

function openListHandler() {
  router.push({ path: `/lists/${selectedList.value.uid}` });
}

// Just initialization stuff, selecting default values and modes
const initialize = () => {
  selectedTab.value = navigationTabItems.value[0].id;
  if (companyLists.value?.length > 0) {
    selectedList.value = companyLists.value[0];
  }

  const dataIsLoaded =
    milestoneAndProgressReportData.value?.companies.length > 0;
  if (dataIsLoaded) {
    prepareData(!proUser.value);
  }

  store.dispatch(CompanyListState.Action.GET_VALUES);
};

// Load new data for the new company list selected
const handleListChange = () => {
  if (proUser.value) {
    if (companyLists.value.length > 0) {
      if (selectedListUid.value) {
        selectedList.value = companyLists.value.filter(
          (l: (typeof companyLists.value)[0]) =>
            l.uid === selectedListUid.value,
        )[0];

        if (selectedList.value?.companies.length > 0) {
          if (proUser.value) {
            store.dispatch(
              MilestoneReportsState.Action.GET_VALUE,
              selectedList?.value.uid,
            );
          } else {
            prepareData(!proUser.value);
          }
        }
      } else {
        selectedListUid.value = companyLists.value[0].uid;
      }
    }
  } else {
    prepareData(!proUser.value);
  }
};

// Take the milestone and level progress data an create suitable
// data to be viewed in the charts and the tables
const prepareData = (prepareDummyData: boolean) => {
  // Chart Data
  levelsChart.value = createLevelsChartData(
    milestoneAndProgressReportData.value,
    prepareDummyData,
  );
  milestonesChart.value = createMilestonesChartData(
    milestoneAndProgressReportData.value,
    prepareDummyData,
  );
  // levelsOverTimeChart.value = createLevelsOverTimeChartData(
  //   milestoneAndProgressReportData.value,
  //   !proUser.value,
  //   // true,
  // );

  // Table data
  highlightsData.value = createHiglightsData(
    milestoneAndProgressReportData.value,
    prepareDummyData,
  );
  assessmentData.value = createAssessmentTableData(
    milestoneAndProgressReportData.value,
    prepareDummyData,
  );
  progressTableData.value = createProgressTableData(
    milestoneAndProgressReportData.value,
    selectedList.value?.title,
    prepareDummyData,
  );
};

onBeforeMount(initialize);
watch(companyLists, handleListChange);
watch(selectedListUid, handleListChange);
watch(milestoneAndProgressReportData, () => prepareData(!proUser.value));
</script>

<style lang="scss" scoped>
.debug {
  background-color: rgba(82, 195, 149, 0.14);
}
.debug2 {
  background-color: rgba(195, 82, 82, 0.14);
}

.milestone-progress-reports {
  header {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    margin-bottom: 40px;

    @include breakpoint-down(lg) {
      flex-direction: column;
      align-items: flex-start;
      gap: 20px;
    }
  }

  &__chart-label {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    span {
      font-size: 15px;
      font-weight: 500;
    }
  }

  // No Lists Available
  &__nolist,
  &__nocompanies {
    background-color: #fff;
    border: 1px solid #e4e6ea;
    width: 100%;
    min-height: calc(100vh - 445px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }

  &__nolist__text {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__nolist__text span:first-child {
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
    color: #272740;
  }

  &__nolist__text span:last-child {
    font-size: 15px;
    font-weight: 500;
    line-height: 25px;
    color: #adb0bd;
  }

  &__nolist__cta img {
    margin-right: 8px;
  }

  &__header {
    ::v-deep .px-navigation-tab {
      background-image: linear-gradient(180deg, #fff, #fafafa);
      justify-content: flex-start;
      padding-top: 32px;
      padding-left: 32px;
      min-width: 100%;
    }
    ::v-deep .px-navigation-tab-item__container {
      padding: 0 20px 12px 20px;
    }
  }

  &__content {
    padding: 32px;
    background-color: #fff;
    border: 1px solid #e4e6ea;
  }

  &__title {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;

    p {
      color: $manatee;
    }
    &-secondary {
      color: $manatee;
    }
  }

  &__charts {
    padding-bottom: 32px;
  }

  &__select {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;

    p {
      min-width: 65px;
    }
  }

  &__data {
    padding: 0 32px;
  }

  &__data__inner {
    background-color: transparent;
    border: 1px solid #e4e6ea;
    border-top: none;
    padding: 0 32px 32px 32px;
  }

  &__data__highlights {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    padding: 48px 0;
  }

  &__highlights__section {
    padding: 0 60px;
    min-width: 33%;
  }

  &__highlights__section:not(:last-child) {
    border-right: 1px solid;
    border-image: linear-gradient(
        to bottom,
        #e4e6ea35 0%,
        #e4e6ea 40%,
        #e4e6ea 60%,
        #e4e6ea35 100%
      )
      1;
  }

  &__highlights__title {
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
    padding-bottom: 4px;
    color: #272740;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &__highlights__sub-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 12px;
  }

  &__highlights__sub-item span:first-child {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #272740;
  }

  &__highlights__sub-item span:nth-child(2) {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #272740;
  }

  &__data__assesment {
    border: 1px solid #e4e6ea;
    background-color: #fff;
    // background-color: green;
    box-shadow: 0 2px 11px 0 #0000000d;
  }

  &__data__assesment__header {
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
    color: #272740;
    padding: 12px 20px;
    background: linear-gradient(to bottom, #fff 0%, #f4f4f4 100%);
  }

  &__data__assesment__table {
    padding: 0 20px;
  }
}
</style>
